import React, { useMemo } from "react";
import { Card, ConfigProvider, DatePicker, Segmented, Tag } from "antd";
import mnMN from "antd/locale/mn_MN";
import { CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import updateLocale from "dayjs/plugin/updateLocale";

dayjs.extend(isoWeek);
dayjs.extend(updateLocale);

dayjs.locale("mn");
dayjs.updateLocale("mn", {
  weekStart: 1,
});

const { RangePicker } = DatePicker;

function normalizeRange(rangeMode, weekValue, customRange) {
  if (rangeMode === "custom") {
    const start = customRange?.[0] ? dayjs(customRange[0]).startOf("day") : null;
    const end = customRange?.[1] ? dayjs(customRange[1]).endOf("day") : null;

    return {
      startDate: start ? start.format("YYYY-MM-DD") : null,
      endDate: end ? end.format("YYYY-MM-DD") : null,
      isValid: Boolean(start && end),
      label: "Хугацаа",
    };
  }

  const week = weekValue ? dayjs(weekValue) : dayjs();
  return {
    startDate: week.startOf("isoWeek").format("YYYY-MM-DD"),
    endDate: week.endOf("isoWeek").format("YYYY-MM-DD"),
    isValid: true,
    label: "Долоо хоног",
  };
}

export default function DateRangeSelectorCard({
  rangeMode,
  onRangeModeChange,
  selectedWeek,
  onSelectedWeekChange,
  customRange,
  onCustomRangeChange,
}) {
  const resolved = useMemo(() => {
    return normalizeRange(rangeMode, selectedWeek, customRange);
  }, [rangeMode, selectedWeek, customRange]);

  return (
    <Card
      className="rounded-[24px] border border-slate-200 shadow-sm"
      styles={{ body: { padding: 18 } }}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <CalendarOutlined />
              <span>{resolved.label}</span>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Tag className="!rounded-full !px-3 !py-1 !text-sm !font-medium">
                {resolved.startDate || "-"}
              </Tag>
              <span className="text-slate-400">→</span>
              <Tag className="!rounded-full !px-3 !py-1 !text-sm !font-medium">
                {resolved.endDate || "-"}
              </Tag>
            </div>
          </div>

          <Segmented
            value={rangeMode}
            onChange={onRangeModeChange}
            options={[
              { label: "7 хоног", value: "week" },
              { label: "Дурын хугацаа", value: "custom" },
            ]}
            className="w-full lg:w-auto"
          />
        </div>

        <ConfigProvider locale={mnMN}>
          {rangeMode === "week" ? (
            <DatePicker
              picker="week"
              showWeek={false}
              value={selectedWeek}
              allowClear={false}
              onChange={(value) => {
                if (value) {
                  onSelectedWeekChange(value);
                }
              }}
              className="h-11 w-full rounded-xl lg:max-w-[320px]"
              format={(value) =>
                `${value.startOf("isoWeek").format("YYYY-MM-DD")} ~ ${value
                  .endOf("isoWeek")
                  .format("YYYY-MM-DD")}`
              }
            />
          ) : (
            <RangePicker
              value={customRange}
              allowClear={false}
              onChange={(values) => {
                if (values?.[0] && values?.[1]) {
                  onCustomRangeChange(values);
                }
              }}
              className="h-11 w-full rounded-xl lg:max-w-[420px]"
              format="YYYY-MM-DD"
            />
          )}
        </ConfigProvider>
      </div>
    </Card>
  );
}

export function resolveDateRange(rangeMode, selectedWeek, customRange) {
  return normalizeRange(rangeMode, selectedWeek, customRange);
}